/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../theme';
import { messageService } from '../services/messageService';
import { 
  MessageSquare, 
  Search, 
  Filter, 
  MoreHorizontal,
  User,
  Clock,
  ChevronRight
} from 'lucide-react';
import { ROUTES } from '../routes';

interface Message {
  id: number;
  senderId: number;
  receiverId: number;
  subject: string;
  body: string;
  createdAt: string;
  senderName?: string;
  receiverName?: string;
}

export default function Inbox() {
  const theme = useTheme();
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const currentUserId = Number(localStorage.getItem('userId'));

  useEffect(() => {
    const fetchMessages = async () => {
      try {
        const response = await messageService.getMessages();
        setMessages(response.data.data || []);
      } catch (err) {
        setError('Failed to load messages.');
      } finally {
        setLoading(false);
      }
    };

    fetchMessages();
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: theme.colors.primary }}></div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-black tracking-tight" style={{ color: theme.colors.text }}>Messages</h1>
          <p style={{ color: theme.colors.mutedText }}>Chat with buyers and sellers about your items.</p>
        </div>
      </div>

      {error && (
        <div className="border px-4 py-3 rounded-xl font-bold" 
             style={{ backgroundColor: theme.colors.danger + '10', borderColor: theme.colors.danger, color: theme.colors.danger }}>
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Sidebar Filters */}
        <div className="lg:col-span-1 space-y-4">
          <div className="p-6 rounded-3xl border space-y-6" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 opacity-30" size={18} />
              <input 
                type="text" 
                placeholder="Search messages..." 
                className="w-full pl-10 pr-4 py-2.5 rounded-xl border focus:outline-none focus:ring-2 transition-all text-sm"
                style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border, color: theme.colors.text }}
              />
            </div>
            
            <div className="space-y-2">
              <button className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl font-bold text-sm transition-all"
                      style={{ backgroundColor: theme.colors.primary + '10', color: theme.colors.primary }}>
                <span>All Messages</span>
                <span className="bg-white px-2 py-0.5 rounded-lg text-[10px] shadow-sm">{messages.length}</span>
              </button>
              <button className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl font-bold text-sm transition-all hover:bg-gray-50"
                      style={{ color: theme.colors.mutedText }}>
                <span>Unread</span>
                <span className="bg-gray-100 px-2 py-0.5 rounded-lg text-[10px]">0</span>
              </button>
              <button className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl font-bold text-sm transition-all hover:bg-gray-50"
                      style={{ color: theme.colors.mutedText }}>
                <span>Archived</span>
              </button>
            </div>
          </div>
        </div>

        {/* Message List */}
        <div className="lg:col-span-3">
          {messages.length === 0 ? (
            <div className="p-20 rounded-3xl border border-dashed text-center space-y-4" 
                 style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
              <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto" style={{ backgroundColor: theme.colors.primary + '10' }}>
                <MessageSquare size={40} style={{ color: theme.colors.primary }} />
              </div>
              <div className="max-w-xs mx-auto">
                <h3 className="text-xl font-bold mb-1" style={{ color: theme.colors.text }}>No messages yet</h3>
                <p style={{ color: theme.colors.mutedText }}>When you contact a seller or a buyer messages you, they'll appear here.</p>
              </div>
            </div>
          ) : (
            <div className="rounded-3xl border overflow-hidden shadow-sm" style={{ backgroundColor: theme.colors.surface, borderColor: theme.colors.border }}>
              <div className="divide-y" style={{ borderColor: theme.colors.border }}>
                {messages.map((msg) => {
                  const isSentByMe = msg.senderId === currentUserId;
                  const otherPartyId = isSentByMe ? msg.receiverId : msg.senderId;
                  
                  return (
                    <Link 
                      key={msg.id} 
                      to={`${ROUTES.MESSAGES}/${msg.id}`} 
                      className="block p-6 hover:bg-gray-50 transition-all group"
                    >
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-2xl flex-shrink-0 flex items-center justify-center border transition-transform group-hover:scale-110"
                             style={{ backgroundColor: theme.colors.background, borderColor: theme.colors.border }}>
                          <User size={24} style={{ color: theme.colors.mutedText }} />
                        </div>
                        <div className="flex-grow min-w-0">
                          <div className="flex justify-between items-center mb-1">
                            <h3 className="font-black text-sm truncate" style={{ color: theme.colors.text }}>
                              {isSentByMe ? `To: User #${msg.receiverId}` : `From: User #${msg.senderId}`}
                            </h3>
                            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-wider opacity-40">
                              <Clock size={12} />
                              {new Date(msg.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <h4 className="font-bold text-blue-500 text-sm mb-1 truncate group-hover:underline">
                            {msg.subject || 'Inquiry about item'}
                          </h4>
                          <p className="text-sm line-clamp-1 opacity-60" style={{ color: theme.colors.text }}>
                            {msg.body}
                          </p>
                        </div>
                        <div className="flex-shrink-0 self-center opacity-0 group-hover:opacity-100 transition-opacity">
                          <ChevronRight size={20} style={{ color: theme.colors.mutedText }} />
                        </div>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
